import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import translate from '../../translate';
import FormInput from '../../forms/FormInput';
import GoogleInput from '../../forms/inputs/GoogleInput';
import VatTypesProp from '../../forms/Address/VatTypesProp';
import CountryProp from '../../forms/Address/CountryProp';
import FormLabel from '../../forms/FormLabel';
import AddressProp from '../../propTypes/addressProp';
import CloseIcon from '../../icons/CloseIcon';
import AddParticipantBtn from './AddParticipantBtn';

const PaymentByCompany = ({
  t,
  countries,
  vatTypes,
  companyBilling,
  onChange,
  errors,
  companyEmails,
  onCompanyEmailsChange,
}) => {
  const { errors: formErrors, emailErrors } = errors;
  const [companyForm, setCompanyForm] = useState(companyBilling);
  useEffect(() => onChange({ ...companyForm }), [companyForm]);

  const [placeholder, setPlaceholder] = useState(t('profile_taxCode'));

  const vatTypesList = useMemo(
    () =>
      vatTypes.map(c => ({
        id: c.taxCode,
        text:
          c.taxType === 'eu_vat'
            ? `${c.flagEmoji} ${c.countryName} VAT number`
            : `${c.flagEmoji} ${c.name}`,
      })),
    [vatTypes],
  );

  /** BEGIN:HANDLE COMPANY BILLING FROM PARENT COMPONENT */
  const handleTaxChange = useCallback(
    value => {
      const taxSelected = vatTypes.find(tax => tax.taxCode === value);
      if (taxSelected) {
        setPlaceholder(taxSelected.placeholder);
        setCompanyForm(prevCompanyForm => ({
          ...prevCompanyForm,
          taxType: taxSelected.taxType,
          taxCode: taxSelected.taxCode,
          address: {
            ...prevCompanyForm.address,
            countryCode: taxSelected.countryCode,
            country: countries.find(
              c =>
                c.code.toUpperCase() === taxSelected.countryCode.toUpperCase(),
            ).name,
          },
        }));
      }
    },
    [setCompanyForm, countries, vatTypes],
  );

  const handleAddress = useCallback(
    value => {
      const address = value;
      if (value.location) {
        delete address.location;
      }
      setCompanyForm(prevCompanyForm => ({
        ...prevCompanyForm,
        address,
      }));
    },
    [setCompanyForm],
  );

  const handleAddressChanges = useCallback(
    field => value => {
      if (field === 'countryCode') {
        setCompanyForm(prevCompanyForm => ({
          ...prevCompanyForm,
          address: {
            ...prevCompanyForm?.address,
            [field]: value,
            country: countries.find(
              c => c.code.toUpperCase() === value.toUpperCase(),
            ).name,
          },
        }));
      } else {
        setCompanyForm(prevCompanyForm => ({
          ...prevCompanyForm,
          address: {
            ...prevCompanyForm?.address,
            [field]: value,
          },
        }));
      }
    },
    [setCompanyForm],
  );

  const handleChange = useCallback(
    field => value => {
      setCompanyForm(prevCompanyForm => ({
        ...prevCompanyForm,
        [field]: value,
      }));
    },
    [setCompanyForm],
  );

  const inputProps = useCallback(
    field => {
      switch (field) {
        case 'taxCode':
          return {
            placeholder: t('profile_taxType'),
            items: vatTypesList,
            dropdownParent: '#paidByCompany',
          };
        case 'countryCode':
          return {
            items: countries.map(c => ({
              id: c.code,
              text: `${c.flagEmoji} ${c.name}`,
            })),
          };
        default:
          return undefined;
      }
    },
    [countries, vatTypes],
  );

  const handleRemoveParticipant = useCallback(
    indexToDelete => {
      if (indexToDelete === 0) {
        onCompanyEmailsChange('email', companyEmails?.emails[0]);
        onCompanyEmailsChange('emails', [
          ...companyEmails?.emails.slice(0, indexToDelete),
          ...companyEmails?.emails.slice(indexToDelete + 1),
        ]);
      } else {
        onCompanyEmailsChange('emails', [
          ...companyEmails?.emails.slice(0, indexToDelete - 1),
          ...companyEmails?.emails.slice(indexToDelete),
        ]);
      }
    },
    [companyEmails, onCompanyEmailsChange],
  );

  const addParticipant = useCallback(() => {
    onCompanyEmailsChange('emails', [...companyEmails.emails, '']);
  }, [companyEmails, onCompanyEmailsChange]);

  const handleChangeEmail = useCallback(
    (index, value) => {
      if (index === 0) {
        onCompanyEmailsChange('email', value);
      } else {
        const emails = [...companyEmails.emails];
        emails[index - 1] = value;
        onCompanyEmailsChange('emails', emails);
      }
    },
    [onCompanyEmailsChange, companyEmails],
  );
  /** END:HANDLE COMPANY BILLING FROM PARENT COMPONENT */

  const handleErrors = field => formErrors?.find(e => e[field]);

  return (
    <div className="payment-by-company-container">
      <div id="paidByCompany">
        <div>
          <div className="company-billing">
            {t('company.billing.address.title')}
          </div>
          <div className="academy-payment-companyEmail-label">
            {t('email.person.responsible')}
          </div>
          {[companyEmails.email, ...companyEmails?.emails]?.map(
            (companyEmail, index) => (
              <div
                className="row academy-payment-companyEmail-container"
                key={`companyEmail-${index}`}
              >
                <div className="col-lg-8 col-9">
                  <FormInput
                    id={`companyEmail-${index}`}
                    name={`companyEmail-${index}`}
                    value={companyEmail || ''}
                    onChange={value => handleChangeEmail(index, value)}
                    containerClassName="col-lg-10 col-12 mobile-padding"
                    placeholder={t('email.person.responsible')}
                    groupProps={{ className: 'row' }}
                    inputProps={{ autoComplete: 'companyEmail-off' }}
                  />
                  {emailErrors?.find(i => i === index) >= 0 && (
                    <span className="m--font-danger">
                      {t('error.valid.email')}
                    </span>
                  )}
                </div>
                <div className="col-lg-2 col-3 remove-email-btn remove-companyEmail">
                  {companyEmails?.emails?.length >= 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveParticipant(index)}
                    >
                      <CloseIcon />
                    </button>
                  )}
                </div>
              </div>
            ),
          )}
          <div>
            <AddParticipantBtn onClick={addParticipant} titleKey="add.email" />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6 col-12">
            <div id="field-name" className="">
              <FormInput
                id="name"
                name="name"
                value={companyForm?.name || ''}
                onChange={handleChange('name')}
                label={t('payment.byCompany.name')}
                placeholder={t('payment.byCompany.name')}
                groupProps={{
                  className: `${handleErrors('name') && 'has-danger'}`,
                }}
              />
              {handleErrors('name') && (
                <span className="m--font-danger">
                  {t('ERROR_FIELD_REQUIRED')}
                </span>
              )}
            </div>
          </div>
          <div className="col-lg-6 col-12">
            <div id="field-phone" className="">
              <FormInput
                id="phone"
                name="phone"
                value={companyForm?.phone || ''}
                onChange={handleChange('phone')}
                label={t('address_phonePrefix')}
                placeholder={t('address_phonePrefix')}
                inputProps={{ autoComplete: 'phone-off' }}
                groupProps={{
                  className: `${handleErrors('phone') && 'has-danger'}`,
                }}
              />
              {handleErrors('phone') && (
                <span className="m--font-danger">
                  {t('ERROR_FIELD_REQUIRED')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6 col-12">
            <div id="field-taxCode" className="">
              <FormInput
                id="taxCode"
                name="taxCode"
                value={companyForm?.taxCode || ''}
                onChange={handleTaxChange}
                label={t('profile_taxType')}
                placeholder={t('profile_taxType')}
                type="select"
                groupProps={{
                  className: `${handleErrors('taxCode') && 'has-danger'}`,
                }}
                inputProps={inputProps('taxCode')}
              />
              {handleErrors('taxCode') && (
                <span className="m--font-danger">
                  {t('ERROR_FIELD_REQUIRED')}
                </span>
              )}
            </div>
          </div>
          <div className="col-lg-6 col-12">
            <div id="field-vatNumber" className="">
              <FormInput
                id="vatNumber"
                name="vatNumber"
                value={companyForm?.vatNumber || ''}
                onChange={handleChange('vatNumber')}
                label={t('profile_taxCode')}
                placeholder={placeholder}
                groupProps={{
                  className: `${handleErrors('vatNumber') && 'has-danger'}`,
                }}
              />
              {handleErrors('vatNumber') && (
                <span className="m--font-danger">
                  {t('ERROR_FIELD_REQUIRED')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6 col-12">
            <div
              className={`form-group m-form__group ${
                handleErrors('address_1') && 'has-danger'
              }`}
            >
              <FormLabel
                id="address_1"
                className="col-form-label"
                label={t('payment.byCompany.street1')}
              />
              <GoogleInput
                name="address_1"
                onChange={handleAddress}
                placeholder={t('payment.byCompany.street1')}
                updateValue={companyForm?.address?.address_1 || ''}
                onSetValue={handleAddressChanges('address_1')}
                filterByCountry={companyForm?.address?.countryCode || null}
              />
              {handleErrors('address_1') && (
                <span className="m--font-danger">
                  {t('ERROR_FIELD_REQUIRED')}
                </span>
              )}
            </div>
          </div>
          <div className="col-lg-6 col-12">
            <div id="field-address_2" className="">
              <FormInput
                id="address_2"
                name="address_2"
                value={companyForm?.address?.address_2 || ''}
                onChange={handleAddressChanges('address_2')}
                label={t('payment.byCompany.street2')}
                placeholder={t('payment.byCompany.street2')}
                groupProps={{
                  className: `${handleErrors('address_2') && 'has-danger'}`,
                }}
                inputProps={{ autoComplete: 'address_2-off' }}
              />
              {handleErrors('address_2') && (
                <span className="m--font-danger">
                  {t('ERROR_FIELD_REQUIRED')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-4 col-12">
            <div id="field-zipCode" className="">
              <FormInput
                id="zipCode"
                name="zipCode"
                value={companyForm?.address?.zipCode || ''}
                onChange={handleAddressChanges('zipCode')}
                label={t('zipCode')}
                placeholder={t('zipCode')}
                groupProps={{
                  className: `${handleErrors('zipCode') && 'has-danger'}`,
                }}
                inputProps={{ autoComplete: 'zipCode-off' }}
              />
              {handleErrors('zipCode') && (
                <span className="m--font-danger">
                  {t('ERROR_FIELD_REQUIRED')}
                </span>
              )}
            </div>
          </div>
          <div className="col-lg-4 col-12">
            <div id="field-city" className="">
              <FormInput
                id="city"
                name="city"
                value={companyForm?.address?.city || ''}
                onChange={handleAddressChanges('city')}
                label={t('city')}
                placeholder={t('city')}
                groupProps={{
                  className: `${handleErrors('city') && 'has-danger'}`,
                }}
                inputProps={{ autoComplete: 'city-off' }}
              />
              {handleErrors('city') && (
                <span className="m--font-danger">
                  {t('ERROR_FIELD_REQUIRED')}
                </span>
              )}
            </div>
          </div>
          <div className="col-lg-4 col-12">
            <div id="field-countryCode" className="">
              <FormInput
                id="company-countryCode"
                name="company-countryCode"
                value={companyForm?.address?.countryCode || ''}
                onChange={handleAddressChanges('countryCode')}
                label={t('address_countryCode')}
                placeholder={t('address_countryCode')}
                type="select"
                groupProps={{
                  className: `${handleErrors('countryCode') && 'has-danger'}`,
                }}
                inputProps={inputProps('countryCode')}
              />
              {handleErrors('countryCode') && (
                <span className="m--font-danger">
                  {t('ERROR_FIELD_REQUIRED')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

PaymentByCompany.propTypes = {
  t: PropTypes.func.isRequired,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  companyBilling: PropTypes.shape({
    name: PropTypes.string,
    emails: PropTypes.arrayOf(PropTypes.string),
    email: PropTypes.string,
    taxCode: PropTypes.string,
    taxType: PropTypes.string,
    vatNumber: PropTypes.string,
    address: PropTypes.shape(AddressProp.propType),
  }),
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(PropTypes.shape({})),
  onCompanyEmailsChange: PropTypes.func.isRequired,
  companyEmails: PropTypes.shape({
    email: PropTypes.string,
    emails: PropTypes.arrayOf(PropTypes.string),
  }),
};

PaymentByCompany.defaultProps = {
  companyBilling: null,
  errors: [],
  companyEmails: {
    email: '',
    emails: [],
  },
};

export default translate(PaymentByCompany);
